import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useAuthState } from "./hooks/use-auth-state";
import { useEntitlement } from "./hooks/use-entitlement";
import AdminPage from "./pages/admin-page";
import CampaignPage from "./pages/campaign-page";
import CampaignSetupPage from "./pages/campaign-setup-page";
import InvitePage from "./pages/invite-page";
import InviteRequiredPage from "./pages/invite-required-page";
import LaunchHubPage from "./pages/launch-hub-page";
import LoginPage from "./pages/login-page";
import LogoutPage from "./pages/logout-page";
import AboutPage from "./pages/marketing/about-page";
import ComingSoonPage from "./pages/marketing/coming-soon-page";
import ContactPage from "./pages/marketing/contact-page";
import FeaturesPage from "./pages/marketing/features-page";
import LandingPage from "./pages/marketing/landing-page";
import StandaloneCharacterCreationPage from "./pages/standalone-character-creation-page";

function FullScreenLoading() {
  return (
    <div className="flex h-dvh items-center justify-center bg-background text-foreground">
      Loading...
    </div>
  );
}

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuthState();
  if (loading) {
    return <FullScreenLoading />;
  }
  if (!user) {
    return <Navigate replace to="/login" />;
  }
  return <>{children}</>;
}

/**
 * Requires an account entitlement ("INVITEE", "PUBLIC", or "ADMIN").
 * Unentitled users are routed to the join/invite screen. Must be nested
 * INSIDE AuthGuard.
 */
function EntitledGuard({ children }: { children: React.ReactNode }) {
  const { entitlement, loading } = useEntitlement(true);
  if (loading) {
    return <FullScreenLoading />;
  }
  if (!entitlement) {
    return <Navigate replace to="/invite-required" />;
  }
  return <>{children}</>;
}

/**
 * Server-verified ADMIN gate (entitlement from /api/auth/me — not merely
 * UI-hidden; the kernel rejects non-admin tool calls regardless).
 */
function AdminGuard({ children }: { children: React.ReactNode }) {
  const { entitlement, loading } = useEntitlement(true);
  if (loading) {
    return <FullScreenLoading />;
  }
  if (entitlement !== "ADMIN") {
    return <Navigate replace to="/launch" />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<LoginPage />} path="/login" />
        <Route element={<LogoutPage />} path="/logout" />
        <Route element={<InvitePage />} path="/invite/:token" />
        <Route
          element={
            <AuthGuard>
              <InviteRequiredPage />
            </AuthGuard>
          }
          path="/invite-required"
        />
        <Route
          element={
            <AuthGuard>
              <AdminGuard>
                <AdminPage />
              </AdminGuard>
            </AuthGuard>
          }
          path="/admin"
        />
        <Route element={<Navigate replace to="/launch" />} path="/campaigns" />
        <Route
          element={
            <AuthGuard>
              <LaunchHubPage />
            </AuthGuard>
          }
          path="/launch"
        />
        <Route
          element={
            <AuthGuard>
              <EntitledGuard>
                <StandaloneCharacterCreationPage />
              </EntitledGuard>
            </AuthGuard>
          }
          path="/characters/new"
        />
        <Route
          element={
            <AuthGuard>
              <EntitledGuard>
                <CampaignSetupPage />
              </EntitledGuard>
            </AuthGuard>
          }
          path="/campaigns/:id/setup"
        />
        <Route
          element={
            <AuthGuard>
              <EntitledGuard>
                <CampaignPage />
              </EntitledGuard>
            </AuthGuard>
          }
          path="/campaigns/:id"
        />
        <Route element={<LandingPage />} path="/" />
        <Route element={<AboutPage />} path="/about" />
        <Route element={<FeaturesPage />} path="/features" />
        <Route element={<ComingSoonPage />} path="/coming-soon" />
        <Route element={<ContactPage />} path="/contact" />
        <Route element={<Navigate replace to="/launch" />} path="*" />
      </Routes>
    </BrowserRouter>
  );
}
